import { ContractFormInfosDto } from '../../../services/dtos/form/contract-form-infos.dto';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { AxiosError } from 'axios';
import { Button, Drawer, Grid, Skeleton } from '@mui/material';
import ContractAttachementForm from '../../../components/forms/contract-attachement/contract-attachement.form';
import { PageContentDto } from '../../../services/dtos/page-content.dto';
import { getPageContent, toPageContentDTO } from '../../../services/page.service';
import logo_service from '../../../assets/images/logo-sddea.png';
import './contract-attachement.page.scss';
import { attachContract } from '../../../services/contracts.service';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { ImageAdmin } from '../../../components/commun/image-admin';

/**
 * Page de rattachement de contrat
 * @constructor
 */
export default function ContractAttachementPage() {
    const navigate = useNavigate();
    const showMessage = useSnackbars();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);

    // Déclaration du loader pour l'affichage du Skeleton
    const [loading, setLoading] = useState<boolean>(true);
    // Bool pour éviter le spam du bouton Valider
    const [validate, setValidate] = useState(false);
    // Gestion de l'état du drawer
    const [drawerOpened, setDrawerOpened] = React.useState(false);

    /**
     * Comportement à la soumission du formulaire
     * @param e Infos du form
     */
    const handlerSubmit = (e: ContractFormInfosDto) => {
        // Désactivation du bouton Valider
        setValidate(true);
        attachContract(e)
            .then(() => {
                showMessage.successAlert('Le contrat a bien été rattaché à votre compte');
                navigate('/');
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                // Réactivation du bouton apès le traitement
                setValidate(false);
            });
    };

    /**
     * Comportement à l'annulation du formulaire
     */
    const handlerCancel = () => {
        // Retour à la page d'accueil
        navigate('/');
    };

    //Création de données par défaut pour la page
    const [pageContent, setPageContent] = useState<PageContentDto>({
        content: undefined,
        title: undefined,
        image: '../../../assets/images/logo-sddea.png',
    });

    // Gestion de l'ouverture du Drawer
    const toggleDrawer = (open: boolean) => () => {
        setDrawerOpened(open);
    };

    // Récupère les données du back permettant de construire la page
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        getPageContent('rattachement-contrat')
            .then(async result => {
                //On met le résultat de la request dans la variable envoyée à la page
                if (result) {
                    setPageContent(await toPageContentDTO(result));
                }
                setLoading(false);
            })
            .catch(error => {
                //Affichage du message d'erreur si la request ne récupère rien
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container spacing={2}>
            {/*Affichage de l'image dans un drawer pour les écrans mobile*/}
            <Grid item xs={12} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {loading ? (
                    // Skeleton pour le chargement de l'image, rend indispo le drawer tant que l'image est pas récupérée
                    <Skeleton variant="text">
                        <Button onClick={toggleDrawer(true)}>Voir un exemple de facture</Button>
                    </Skeleton>
                ) : (
                    pageContent?.image && (
                        <>
                            <Button onClick={toggleDrawer(true)}>Voir un exemple de facture</Button>
                            <Drawer open={drawerOpened} anchor={'bottom'} onClose={toggleDrawer(false)}>
                                <ImageAdmin
                                    className="image-contract"
                                    src={pageContent.image}
                                    alt="Une facture du SDDEA"
                                />
                            </Drawer>
                        </>
                    )
                )}
            </Grid>
            {/*Formulaire*/}
            <Grid item md={6} xs={12}>
                <ContractAttachementForm
                    handlerSubmit={handlerSubmit}
                    validate={validate}
                    handlerCancel={handlerCancel}
                    logged={true}
                />
            </Grid>
            {/*Affichage de l'image sur le côté du formulaire pour les écrans normaux*/}
            <Grid item md={6} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {loading ? (
                    <Skeleton variant="rectangular">
                        <img className="image-contract" src={logo_service} alt="Placeholder de chargement" />
                    </Skeleton>
                ) : (
                    pageContent?.image && (
                        <ImageAdmin className="image-contract" src={pageContent.image} alt="Une facture du SDDEA" />
                    )
                )}
            </Grid>
        </Grid>
    );
}
